import { useCallback, useEffect, useRef, useState } from "react";

import { getNextActiveCubePercentage, INACTIVE_TO_ACTIVE_CUBE_TRANSITIONS } from "~/utils/onboarding/animation";
import { cubeActivationBatchCount, cubeActivationBatchDelay } from "~/utils/onboarding/constants";
import { determineCubeVisibility } from "~/utils/onboarding/puzzle-map-algorithm";
import { shuffleCubes, shuffleIndices } from "~/utils/onboarding/shuffle-cubes";

export interface Cube {
  id: string;
  idx: number;
  isActive: boolean;
  isVisible: boolean;
}

export interface GridDimensions {
  columnsPerRow: number;
  numberOfRows: number;
  totalCubes: number;
}

interface PuzzleConfig {
  progress: number;
  gridDimensions: GridDimensions;
  enableAutoAnimation?: boolean;
}

export function useOnboardingCubes({ progress, gridDimensions, enableAutoAnimation }: PuzzleConfig): Cube[] {
  const [cubes, setCubes] = useState<Cube[]>([]);
  const [shouldAnimateOnboarding, setShouldAnimateOnboarding] = useState(false);
  const cubesRef = useRef<Cube[]>([]);
  const autoAnimationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const onboardingAnimationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const progressCompleteTimeRef = useRef<number | null>(null);

  useEffect(() => {
    cubesRef.current = cubes;
  }, [cubes]);

  const activateCubesInBatches = useCallback(() => {
    const currentCubes = cubesRef.current;
    const batchCount = cubeActivationBatchCount;
    const batchDelay = cubeActivationBatchDelay;
    const cubesPerBatch = Math.ceil(currentCubes.length / batchCount);

    const availableCubes = currentCubes.filter((cube) => !cube.isActive);
    const timeoutIds: NodeJS.Timeout[] = [];
    let isAnimating = true;

    const activateBatch = (batchIndex: number) => {
      if (!isAnimating || batchIndex >= batchCount || availableCubes.length === 0) return;

      const batchSize = Math.min(cubesPerBatch, availableCubes.length);
      const selectedCubes = shuffleCubes(availableCubes, availableCubes.length, batchSize);

      setCubes((prevCubes) => {
        const newCubes = [...prevCubes];
        selectedCubes.forEach((selectedCube) => {
          const index = newCubes.findIndex((cube) => cube.id === selectedCube?.id);
          if (index !== -1 && !newCubes[index]?.isActive) {
            const cube = newCubes[index];
            if (cube) {
              newCubes[index] = { ...cube, isActive: true };
            }
          }
        });
        return newCubes;
      });
    };

    for (let i = 0; i < batchCount; i++) {
      const timeoutId = setTimeout(() => activateBatch(i), (i + 1) * batchDelay);
      timeoutIds.push(timeoutId);
    }

    return () => {
      isAnimating = false;
      timeoutIds.forEach((id) => clearTimeout(id));
    };
  }, []);

  const autoPlayAnimation = useCallback(() => {
    let isAnimating = true;

    const getInactiveCubePercentage = () => {
      const currentCubes = cubesRef.current;
      const inactiveCubes = currentCubes.filter((cube) => !cube.isActive);
      return Math.round((inactiveCubes.length / currentCubes.length) * 100);
    };

    const setActiveCubesPercentage = (activePercentage: number) => {
      const totalCubes = cubesRef.current.length;
      const targetActiveCubes = Math.round((activePercentage / 100) * totalCubes);
      setCubes((prevCubes) => {
        const newCubes = [...prevCubes];
        newCubes.forEach((cube) => {
          cube.isActive = false;
        });

        const shuffledIndices = shuffleIndices(totalCubes);
        shuffledIndices.slice(0, targetActiveCubes).forEach((index) => {
          if (newCubes[index]) {
            newCubes[index].isActive = true;
          }
        });

        return newCubes;
      });
    };

    const handleAnimation = () => {
      const inactivePercentage = getInactiveCubePercentage();
      const activePercentage = getNextActiveCubePercentage(
        inactivePercentage as keyof typeof INACTIVE_TO_ACTIVE_CUBE_TRANSITIONS,
      );

      setActiveCubesPercentage(activePercentage);
    };

    if (autoAnimationIntervalRef.current) {
      clearInterval(autoAnimationIntervalRef.current);
    }

    handleAnimation();
    autoAnimationIntervalRef.current = setInterval(() => {
      if (!isAnimating) return;
      handleAnimation();
    }, 3000);

    return () => {
      isAnimating = false;
      if (autoAnimationIntervalRef.current) {
        clearInterval(autoAnimationIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const generateCubes = () => {
      const newCubes = Array.from({ length: gridDimensions.totalCubes }).map((_, i) => {
        const row = Math.floor(i / gridDimensions.columnsPerRow);
        const col = i % gridDimensions.columnsPerRow;
        return {
          id: `${row}-${col}`,
          idx: i,
          isActive: false,
          isVisible: determineCubeVisibility({ row, col }, gridDimensions, progress, enableAutoAnimation),
        };
      });

      setCubes((prevCubes) => (JSON.stringify(newCubes) === JSON.stringify(prevCubes) ? prevCubes : newCubes));
    };

    generateCubes();
  }, [progress, gridDimensions, enableAutoAnimation]);

  useEffect(() => {
    if (enableAutoAnimation && cubes.length > 0) {
      const cleanup = autoPlayAnimation();
      return () => {
        cleanup();
        if (autoAnimationIntervalRef.current) clearInterval(autoAnimationIntervalRef.current);
      };
    }
  }, [enableAutoAnimation, cubes.length, autoPlayAnimation]);

  useEffect(() => {
    if (!enableAutoAnimation && cubes.length > 0) {
      const timeoutRef = onboardingAnimationTimeoutRef.current;
      if (progress === 100) {
        if (!progressCompleteTimeRef.current) {
          progressCompleteTimeRef.current = Date.now();
          if (timeoutRef) clearTimeout(timeoutRef);
          setShouldAnimateOnboarding(true);
        }
      } else {
        progressCompleteTimeRef.current = null;
        setShouldAnimateOnboarding(false);
        if (timeoutRef) clearTimeout(timeoutRef);
      }

      return () => {
        if (timeoutRef) clearTimeout(timeoutRef);
      };
    }
  }, [progress, enableAutoAnimation, cubes.length]);

  useEffect(() => {
    if (shouldAnimateOnboarding && cubes.length > 0) {
      const cleanup = activateCubesInBatches();
      return cleanup;
    }
  }, [shouldAnimateOnboarding, activateCubesInBatches, cubes.length]);

  return cubes;
}
